/**
 * Theme selection. Three states, not two: "system" means we stamp nothing on <html> and
 * let the `prefers-color-scheme` block in theme.css decide.
 */
export const THEME_STORAGE_KEY = 'dc.theme';

export type ThemeChoice = 'system' | 'light' | 'dark';

export function readStoredTheme(storage: Pick<Storage, 'getItem'>): ThemeChoice {
  try {
    const raw = storage.getItem(THEME_STORAGE_KEY);
    return raw === 'light' || raw === 'dark' ? raw : 'system';
  } catch {
    // Private-mode browsers throw on storage access; system default is a fine answer.
    return 'system';
  }
}

/** Cycles system -> dark -> light -> system, which is what the single toggle button does. */
export function nextTheme(current: ThemeChoice): ThemeChoice {
  switch (current) {
    case 'system':
      return 'dark';
    case 'dark':
      return 'light';
    case 'light':
      return 'system';
  }
}

/** Writes the choice through to <html data-theme> and to storage. */
export function applyTheme(choice: ThemeChoice, root: HTMLElement, storage: Pick<Storage, 'setItem' | 'removeItem'>): void {
  if (choice === 'system') {
    delete root.dataset['theme'];
  } else {
    root.dataset['theme'] = choice;
  }
  try {
    if (choice === 'system') storage.removeItem(THEME_STORAGE_KEY);
    else storage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    // Non-fatal: the theme still applies for this page load.
  }
}
